'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useEffect, useRef, useState } from 'react';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(' ');
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    lines.push(current);
  }
  return lines;
}

const GRADIENTS: { id: string; label: string; stops: [string, string, string] }[] = [
  { id: 'sunset', label: 'Sunset', stops: ['#feda75', '#d62976', '#4f5bd5'] },
  { id: 'ocean', label: 'Ocean', stops: ['#2af598', '#08aeea', '#4f5bd5'] },
  { id: 'grape', label: 'Grape', stops: ['#a18cd1', '#8e2de2', '#4a00e0'] },
  { id: 'ember', label: 'Ember', stops: ['#ff9a9e', '#f6416c', '#c31432'] },
  { id: 'night', label: 'Night', stops: ['#434343', '#232526', '#000000'] },
];

export default function BrowserInstagramStoryGenerator() {
  const [username, setUsername] = useState('cleanor.app');
  const [storyText, setStoryText] = useState('Runs entirely in your browser.\nNo uploads.');
  const [gradientId, setGradientId] = useState('sunset');
  const [textColor, setTextColor] = useState('#ffffff');
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [avatar, setAvatar] = useState<HTMLImageElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  useToolUsageReport('instagram-story-generator', Boolean(downloadUrl));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function handleFile(file: File | undefined, apply: (img: HTMLImageElement) => void) {
    if (!file || typeof window === 'undefined') return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      apply(img);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Instagram serves stories at 1080x1920. Exporting at 720x1280 left the app to
    // upscale every story, so render at 3x the 360x640 layout instead of 2x.
    const scale = 3;
    const w = 360;
    const h = 640; // 9:16, so 1080x1920 at scale 3
    canvas.width = w * scale;
    canvas.height = h * scale;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // background: image (cover) or gradient
    if (image) {
      const ratio = Math.max(w / image.naturalWidth, h / image.naturalHeight);
      const iw = image.naturalWidth * ratio;
      const ih = image.naturalHeight * ratio;
      ctx.drawImage(image, (w - iw) / 2, (h - ih) / 2, iw, ih);
    } else {
      const preset = GRADIENTS.find((g) => g.id === gradientId) ?? GRADIENTS[0];
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, preset.stops[0]);
      grad.addColorStop(0.5, preset.stops[1]);
      grad.addColorStop(1, preset.stops[2]);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }

    // top progress bar (segmented)
    const segments = 3;
    const gap = 4;
    const barY = 12;
    const barPad = 10;
    const barW = (w - barPad * 2 - gap * (segments - 1)) / segments;
    for (let i = 0; i < segments; i += 1) {
      ctx.fillStyle = i === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)';
      const x = barPad + i * (barW + gap);
      ctx.beginPath();
      ctx.roundRect(x, barY, barW, 3, 2);
      ctx.fill();
    }

    // header chrome: avatar + username + timestamp
    const headerY = 34;
    const avatarR = 16;
    const avatarCx = barPad + avatarR;
    const avatarCy = headerY + avatarR;
    ctx.save();
    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy, avatarR, 0, Math.PI * 2);
    ctx.closePath();
    if (avatar) {
      ctx.clip();
      const ratio = Math.max(
        (avatarR * 2) / avatar.naturalWidth,
        (avatarR * 2) / avatar.naturalHeight,
      );
      const aw = avatar.naturalWidth * ratio;
      const ah = avatar.naturalHeight * ratio;
      ctx.drawImage(avatar, avatarCx - aw / 2, avatarCy - ah / 2, aw, ah);
    } else {
      ctx.fillStyle = '#dd2a7b';
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 16px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((username.trim()[0] || '?').toUpperCase(), avatarCx, avatarCy + 1);
    }
    ctx.restore();
    // avatar ring
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy, avatarR + 1, 0, Math.PI * 2);
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.font = '600 14px system-ui, sans-serif';
    ctx.fillText(username.trim() || 'username', avatarCx + avatarR + 10, avatarCy - 1);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.font = '400 13px system-ui, sans-serif';
    const nameWidth = ctx.measureText(username.trim() || 'username').width;
    ctx.font = '600 14px system-ui, sans-serif';
    const nameW2 = ctx.measureText(username.trim() || 'username').width;
    ctx.font = '400 13px system-ui, sans-serif';
    ctx.fillText('· 2h', avatarCx + avatarR + 10 + nameW2 + 6, avatarCy - 1);
    void nameWidth;

    // story text overlay (centered vertically)
    const overlay = storyText.trim();
    if (overlay) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '700 26px system-ui, sans-serif';
      const lines = wrap(ctx, overlay, w - 48);
      const lineH = 34;
      const totalH = lines.length * lineH;
      let ty = h / 2 - totalH / 2 + lineH / 2;
      for (const line of lines) {
        // readable pill behind each line
        const metrics = ctx.measureText(line);
        const pillW = metrics.width + 24;
        const pillH = lineH - 4;
        ctx.fillStyle = 'rgba(0,0,0,0.28)';
        ctx.beginPath();
        ctx.roundRect(w / 2 - pillW / 2, ty - pillH / 2, pillW, pillH, 8);
        ctx.fill();
        ctx.fillStyle = textColor;
        ctx.fillText(line, w / 2, ty + 1);
        ty += lineH;
      }
    }

    canvas.toBlob((blob) => {
      if (blob) {
        setDownloadUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      }
    }, 'image/png');
  }, [username, storyText, gradientId, textColor, image, avatar]);

  useEffect(() => {
    return () => {
      setDownloadUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">Instagram story generator</h2>
        <p className="tool-live-subtitle">
          Design a mock 9:16 Instagram Story with a background, avatar, username, and text overlay,
          then download it as a PNG. Rendered in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Username</span>
              <input onChange={(e) => setUsername(e.target.value)} type="text" value={username} />
            </label>
            <label className="tool-live-field">
              <span>Background gradient</span>
              <select
                disabled={Boolean(image)}
                onChange={(e) => setGradientId(e.target.value)}
                value={gradientId}
              >
                {GRADIENTS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Story text</span>
              <textarea onChange={(e) => setStoryText(e.target.value)} rows={2} value={storyText} />
            </label>
            <label className="tool-live-field">
              <span>Text color</span>
              <input
                onChange={(e) => setTextColor(e.target.value)}
                type="color"
                value={textColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Avatar image (optional)</span>
              <input
                accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0], setAvatar)}
                type="file"
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Background image (optional)</span>
              <input
                accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0], setImage)}
                type="file"
              />
            </label>
            {image ? (
              <div className="tool-live-field tool-live-field-full">
                <button
                  className="button button-secondary"
                  onClick={() => setImage(null)}
                  type="button"
                >
                  Remove background image
                </button>
              </div>
            ) : null}
          </div>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download="instagram-story.png"
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              width: 260,
              height: 'auto',
              borderRadius: 16,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and marketing.
          </p>
        </section>
      </div>
    </div>
  );
}
